import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, CircularProgress, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import type { PopoverProps } from "@mui/material/Popover";
import { type FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import type { NurseInShift } from "../../utils/models";
import { getShiftIndexByShiftTimes } from "../../utils/utilFunctions";

type Props = {
	nurseInShift: NurseInShift;
	anchorEl?: PopoverProps["anchorEl"];
	open: boolean;
	onClose: () => void;
};

export const SynergyNurseInShiftMenu: FC<Props> = ({ nurseInShift, anchorEl, open, onClose }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { removeNurseFromSpecificDate, getWeekIndexByDateTime, getDayIndexByDateTime } = useHospitalData();
	const { removeNurse, isLoading: isLoadingTypes } = useHandleNurseChange();
	const { removeNurse: isDeleteLoading } = isLoadingTypes;
	const [confirmingDelete, setConfirmingDelete] = useState(false);

	const handleClose = useCallback(() => {
		setConfirmingDelete(false);
		onClose();
	}, [onClose]);

	const handleDeleteClick = useCallback(() => {
		setConfirmingDelete(true);
	}, []);

	const handleDeleteConfirm = useCallback(async () => {
		const dayIndex = getDayIndexByDateTime(nurseInShift.startTime);
		const weekIndex = getWeekIndexByDateTime(nurseInShift.startTime);
		const shiftIndex = getShiftIndexByShiftTimes({
			startTime: nurseInShift.startTime,
			endTime: nurseInShift.endTime,
		});
		// Remove from DB
		await removeNurse({
			nurse: nurseInShift,
			shiftIndex,
			dayNo: dayIndex,
			weekIndex,
			isActual: false,
		});

		// Update local state
		removeNurseFromSpecificDate(weekIndex, dayIndex, shiftIndex, nurseInShift.nurseId, false, nurseInShift.id);

		track("synergy_nurse_in_shift_deleted", {
			nurse_id: nurseInShift.nurseId,
			shift_start_time: nurseInShift.startTime.toISO(),
			shift_end_time: nurseInShift.endTime.toISO(),
			shift_id: nurseInShift.id,
		});

		setConfirmingDelete(false);
		onClose();
	}, [nurseInShift, getDayIndexByDateTime, getWeekIndexByDateTime, removeNurse, removeNurseFromSpecificDate, onClose, track]);

	const handleCancelConfirm = useCallback(() => {
		setConfirmingDelete(false);
	}, []);

	useEffect(() => {
		track("synergy_nurse_in_shift_menu_opened");
	}, [track]);

	return (
		<Menu
			anchorEl={anchorEl}
			open={open}
			onClose={handleClose}
			slotProps={{
				list: {
					dense: true,
					autoFocusItem: open,
				},
			}}
			key="SynergyNurseInShiftMenu"
		>
			{confirmingDelete ? (
				<MenuItem key={"confirm-delete"} disableRipple={true} disableTouchRipple={true}>
					<Box sx={{ display: "flex", gap: 2 }}>
						<ListItemText primary={t("confirm")} sx={{ color: "error.main" }} />
						<ListItemButton
							key={"confirmDelete"}
							dense={true}
							sx={{
								color: "error.main",
								padding: 0,
								justifyContent: "center",
							}}
							tabIndex={0}
							onClick={handleDeleteConfirm}
							disabled={isDeleteLoading}
						>
							<CheckIcon />
						</ListItemButton>
						<ListItemButton dense={true} sx={{ padding: 0, justifyContent: "center" }} tabIndex={0} onClick={handleCancelConfirm}>
							<CloseIcon />
						</ListItemButton>
					</Box>
				</MenuItem>
			) : (
				<MenuItem key={"delete"} onClick={handleDeleteClick} sx={{ color: "error.main" }} disabled={isDeleteLoading}>
					<ListItemIcon sx={{ color: "inherit" }}>
						<DeleteIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={t("delete")} />
					{isDeleteLoading && <CircularProgress size={"12px"} sx={{ ml: 1 }} />}
				</MenuItem>
			)}
		</Menu>
	);
};
